/**
 * Vendor Service — Vendor identity & auth helpers
 * Resolves vendor identity from vendor_users → vendor_profiles OR vendors table.
 * vendor_master role = full access to all vendor portal modules & data.
 */
import { supabase } from '@/integrations/supabase/client';

export interface VendorProfile {
  userId: string;
  vendorId: string;
  vendorName: string;
  vendorCode: string;
  services: string[];
  /** True when user has vendor_master role — sees all data across all vendors */
  isVendorMaster: boolean;
}

/** Roles that grant access to the Vendor Portal */
const VENDOR_PORTAL_ROLES = ['vendor_master', 'vendor'] as const;

/**
 * Resolve the vendor profile for the current authenticated user.
 * Priority: vendor_master check → vendor_users → user_metadata → has_role('vendor')
 */
export async function resolveVendorProfile(userId: string): Promise<VendorProfile | null> {
  const { data: { user: authUser } } = await supabase.auth.getUser();
  console.log('[VendorAuth] ===== VENDOR IDENTITY RESOLUTION =====');
  console.log('[VendorAuth] Auth userId:', userId);
  console.log('[VendorAuth] Auth email:', authUser?.email);

  // Check vendor_master first — full access role
  const { data: isVendorMaster } = await supabase.rpc('has_role', {
    _user_id: userId,
    _role: 'vendor_master',
  });

  if (isVendorMaster === true) {
    console.log('[VendorAuth] ✅ vendor_master role detected — full access granted');
    return {
      userId,
      vendorId: '__vendor_master__',
      vendorName: 'Vendor Master',
      vendorCode: 'MASTER',
      services: ['transport', 'tour', 'stay', 'interpreter', 'daily_rental'],
      isVendorMaster: true,
    };
  }

  // Strategy 1: vendor_users → vendors (canonical path, supports multi-vendor)
  try {
    const { data: mappings, error } = await supabase
      .from('vendor_users' as any)
      .select('vendor_id')
      .eq('user_id', userId);

    if (!error && mappings && (mappings as any[]).length > 0) {
      const vendorIds = (mappings as any[]).map((m: any) => m.vendor_id).filter(Boolean);
      const primaryVendorId = vendorIds[0];
      console.log('[VendorAuth] Found vendor_users mappings, vendor_ids:', vendorIds);

      // Resolve vendor name from vendors table (use first/primary)
      const { data: vendor } = await supabase
        .from('vendors' as any)
        .select('id, name')
        .eq('id', primaryVendorId)
        .maybeSingle();

      if (vendor) {
        console.log('[VendorAuth] ✅ Resolved via vendor_users → vendors:', {
          userId, vendorId: (vendor as any).id, vendorName: (vendor as any).name,
          allVendorIds: vendorIds,
        });
        return {
          userId,
          vendorId: (vendor as any).id,
          vendorName: (vendor as any).name,
          vendorCode: '',
          services: ['transport', 'tour'],
          isVendorMaster: false,
        };
      }

      // Vendors table inaccessible (RLS) — use vendor_id directly
      console.log('[VendorAuth] ✅ Using vendor_id directly (vendors table inaccessible):', primaryVendorId);
      return {
        userId,
        vendorId: primaryVendorId,
        vendorName: 'Vendor',
        vendorCode: '',
        services: ['transport', 'tour'],
        isVendorMaster: false,
      };
    }

    if (error) {
      console.warn('[VendorAuth] vendor_users query error:', error.message);
    }
  } catch (e) {
    console.warn('[VendorAuth] vendor_users lookup failed:', e);
  }

  // Strategy 3: user metadata fallback
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const vendorId = user?.user_metadata?.vendor_id;
    if (vendorId) {
      const { data: vendor } = await supabase
        .from('vendors' as any)
        .select('id, name')
        .eq('id', vendorId)
        .maybeSingle();

      if (vendor) {
        return {
          userId,
          vendorId,
          vendorName: (vendor as any)?.name || 'Vendor',
          vendorCode: '',
          services: ['transport'],
          isVendorMaster: false,
        };
      }
    }
  } catch {
    // ignore
  }

  // Strategy 4: has_role check for regular vendor
  const { data: hasVendorRole } = await supabase.rpc('has_role', {
    _user_id: userId,
    _role: 'vendor',
  });

  if (!hasVendorRole) {
    console.log('[VendorAuth] User has no vendor role:', userId);
    return null;
  }

  console.warn('[VendorAuth] ⚠️ User has vendor role but no vendor_users mapping:', userId);
  return {
    userId,
    vendorId: 'pending_setup',
    vendorName: 'Vendor (Setup Required)',
    vendorCode: '',
    services: [],
    isVendorMaster: false,
  };
}
